/**
 * La acción del estado "sin vincular".
 *
 * La regla del panel es que un registro sin id externo **no se dibuja como link
 * roto**: se dibuja el estado y **la acción de vincularlo**, igual que un KPI sin
 * dato lleva su "Cargalo acá". Antes de esto solo `/revenue` cumplía, porque era
 * la única pantalla que se había escrito su propio handler; los otros catorce
 * estados mostraban el texto y nada más.
 *
 * Qué es "la acción", concretamente. Los ids externos se administran en `/admin`
 * → fuentes de datos, y `/admin` es solo para el rol admin. Así que la acción es
 * distinta según quién la aprieta, y las dos terminan en algún lado:
 *
 *  - **Admin**: lo lleva a `/admin` con el aviso de qué registro vincular. Es
 *    exactamente lo que ya hacía `/revenue`, movido acá para que no se escriba
 *    una vez por pantalla.
 *  - **Ops y comercial**: deja el pedido armado en el portapapeles y dice a
 *    quién pasárselo. Un botón que los mandara a `/admin` los dejaría en la
 *    pantalla de acceso denegado, y uno que solo dijera "pedíselo a un admin"
 *    no sería una acción.
 *
 * No abre un formulario porque **hoy no hay endpoint que escriba ids externos**.
 * Un botón que no puede terminar lo que promete es peor que el estado vacío solo.
 */
import { useNavigate } from 'react-router-dom'

import { useToast } from '../shell/AppShell'
import { useMe } from './queries'

/**
 * Qué falta vincular, dicho entero: `"el lead en Close de Armytech"`. Va tal
 * cual al aviso y al portapapeles, así que se escribe como se lo pediría una
 * persona a otra, no como una clave.
 */
export type Vincular = (que: string) => void

export function textoDelPedido(que: string): string {
  return `Vincular ${que}`
}

/**
 * Handler para pasarle a `ExternalLink.onVincular` (y a `SistemasDeOrigen`).
 *
 * Se usa desde el componente que dibuja el estado, no desde el orquestador de la
 * pantalla: así ninguna tabla tiene que enhebrar un callback por cinco niveles de
 * props para que una celda ofrezca su acción.
 */
export function useVincular(): Vincular {
  const { push } = useToast()
  const navegar = useNavigate()
  const me = useMe()
  const esAdmin = me.data?.rol === 'admin'

  return (que) => {
    const texto = textoDelPedido(que)

    if (esAdmin) {
      push('ok', `${texto}: cargá el id en Admin → fuentes de datos`)
      navegar('/admin')
      return
    }

    const aviso = (copiado: boolean) => {
      push(
        'ok',
        copiado
          ? `Pedido copiado — «${texto}». Los ids externos los carga un Admin en Admin → fuentes de datos.`
          : `Pedile a un Admin: «${texto}». Los ids externos se cargan en Admin → fuentes de datos.`,
      )
    }

    // Sin permiso de portapapeles —o sin portapapeles, como en los tests— el
    // aviso sigue diciendo qué pedir y a quién: la acción no depende de copiar.
    try {
      void navigator.clipboard
        .writeText(texto)
        .then(() => aviso(true))
        .catch(() => aviso(false))
    } catch {
      aviso(false)
    }
  }
}
